// src/utils/cache.js

const StorageType = {
  LOCAL: 'localStorage',
  SESSION: 'sessionStorage',
  MEMORY: 'memoryStorage'
}

class Cache {
  /**
   * 缓存构造函数
   * @param {Object} options 配置选项
   * @param {StorageType} [options.storage=StorageType.LOCAL] 存储类型
   * @param {string} [options.namespace='app_'] 命名空间前缀
   * @param {number} [options.defaultTTL=0] 默认过期时间（秒）
   */
  constructor({
    storage = StorageType.LOCAL,
    namespace = 'app_',
    defaultTTL = 0
  } = {}) {
    this.storage = storage
    this.namespace = namespace
    this.defaultTTL = defaultTTL
    this.memoryCache = new Map() // 内存存储容器
  }

  // 获取完整存储键名
  _getStorageKey(key) {
    return `${this.namespace}${key}`
  }

  // 序列化存储值（包含元数据）
  _serialize(value, ttl = this.defaultTTL) {
    const data = {
      value,
      _expire: ttl ? Date.now() + ttl * 1000 : 0
    }
    return JSON.stringify(data)
  }

  // 反序列化存储值
  _deserialize(rawValue, key) {
    try {
      const data = JSON.parse(rawValue)
      if (data._expire && Date.now() > data._expire) {
        this.remove(key) // 自动清理过期项
        return null
      }
      return data.value
    } catch (e) {
      return null
    }
  }

  // 获取存储引擎
  _getStorageEngine() {
    if (this.storage === StorageType.MEMORY) {
      return {
        getItem: (key) => this.memoryCache.get(key),
        setItem: (key, value) => this.memoryCache.set(key, value),
        removeItem: (key) => this.memoryCache.delete(key),
        clear: () => this.memoryCache.clear()
      }
    }
    return window[this.storage]
  }

  /**
   * 设置缓存
   * @param {string} key 缓存键
   * @param {any} value 缓存值
   * @param {number} [ttl] 过期时间（秒）
   */
  set(key, value, ttl) {
    if (value === undefined || value === null) {
      return this.remove(key)
    }

    const storage = this._getStorageEngine()
    const storageKey = this._getStorageKey(key)
    const serialized = this._serialize(value, ttl ?? this.defaultTTL)

    try {
      storage.setItem(storageKey, serialized)
    } catch (e) {
      // 处理存储空间不足的情况
      console.error('Storage failed:', e)
      if (this.storage !== StorageType.MEMORY) {
        this.clearOldItems()
        storage.setItem(storageKey, serialized)
      }
    }
  }

  /**
   * 获取缓存
   * @param {string} key 缓存键
   * @param {any} defaultValue 默认值
   * @returns {any}
   */
  get(key, defaultValue = null) {
    const storage = this._getStorageEngine()
    const storageKey = this._getStorageKey(key)
    const rawValue = storage.getItem(storageKey)

    if (!rawValue) return defaultValue
    return this._deserialize(rawValue, key) ?? defaultValue
  }

  // 删除指定缓存
  removeItem(key) {
    const storage = this._getStorageEngine()
    const storageKey = this._getStorageKey(key)
    storage.removeItem(storageKey)
  }

  // 清空当前命名空间下所有缓存
  clear() {
    const storage = this._getStorageEngine()
    if (this.storage === StorageType.MEMORY) {
      storage.clear()
      return
    }

    Object.keys(storage).forEach(key => {
      if (key.startsWith(this.namespace)) {
        storage.removeItem(key)
      }
    })
  }

  // 清理过期项目
  clearOldItems() {
    const storage = this._getStorageEngine()
    Object.keys(storage).forEach(key => {
      if (key.startsWith(this.namespace)) {
        const rawValue = storage.getItem(key)
        if (!this._deserialize(rawValue)) {
          storage.removeItem(key)
        }
      }
    })
  }
}

// 默认导出实例（可按需创建多个实例）
export default new Cache()

// 导出构造函数用于自定义实例
export { Cache, StorageType }